import { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiFilter, FiLink, FiMapPin } from "react-icons/fi";
import { MdVerified } from "react-icons/md";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioConformidadeRastreabilidade = () => {
  const [dados, setDados] = useState({
    resumo: {
      total_insumos_aplicados: 0,
      total_animais_rastreados: 0,
      total_lotes_produzidos: 0,
      ultima_atualizacao: "",
    },
    insumos: [],
    animais: [],
    lotes: [],
    rastreamento_por_lote: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("insumos");

  const [culturas, setCulturas] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  const [safras, setSafras] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    culturas: useRef(null),
    talhoes: useRef(null),
    animais: useRef(null),
    lotes: useRef(null),
    fazendas: useRef(null),
    safras: useRef(null),
  };

  const [filtros, setFiltros] = useState({
    culturas: [],
    talhoes: [],
    animais: [],
    lotes: [],
    fazendas: [],
    safras: [],
    data_inicio: "",
    data_fim: "",
    tipo_rastreio: "todos", // insumos, animais, lotes
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        dropdownRefs[openDropdown]?.current &&
        !dropdownRefs[openDropdown].current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [
        culturasRes,
        talhoesRes,
        animaisRes,
        lotesRes,
        fazendasRes,
        safrasRes,
      ] = await Promise.all([
        api.get("/agricultura/culturas/"),
        api.get("/fazendas/talhoes/"),
        listarTudo("/pecuaria/animal/"),
        api.get("/pecuaria/lotes/"),
        api.get("/fazendas/fazendas/"),
        api.get("/agricultura/safras/"),
      ]);

      setCulturas(culturasRes.data?.results || []);
      setTalhoes(talhoesRes.data?.results || []);
      setAnimais(Array.isArray(animaisRes.data) ? animaisRes.data : animaisRes.data?.results || []);
      setLotes(lotesRes.data?.results || []);
      setFazendas(fazendasRes.data?.results || []);
      setSafras(safrasRes.data?.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros((prev) => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options) && options.length > 0) {
      const allIds = options.map((opt) => opt.id);
      setFiltros((prev) => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros((prev) => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value,
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.culturas?.length > 0)
        params.culturas = filtros.culturas.join(",");
      if (filtros.talhoes?.length > 0)
        params.talhoes = filtros.talhoes.join(",");
      if (filtros.animais?.length > 0)
        params.animais = filtros.animais.join(",");
      if (filtros.lotes?.length > 0) params.lotes = filtros.lotes.join(",");
      if (filtros.fazendas?.length > 0)
        params.fazendas = filtros.fazendas.join(",");
      if (filtros.safras?.length > 0) params.safras = filtros.safras.join(",");
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.tipo_rastreio && filtros.tipo_rastreio !== "todos")
        params.tipo_rastreio = filtros.tipo_rastreio;

      const response = await api.get(
        "/relatorios/conformidade/rastreabilidade/",
        { params },
      );

      setDados(
        response.data || {
          resumo: {
            total_insumos_aplicados: 0,
            total_animais_rastreados: 0,
            total_lotes_produzidos: 0,
            ultima_atualizacao: new Date().toISOString(),
          },
          insumos: [],
          animais: [],
          lotes: [],
          rastreamento_por_lote: [],
        },
      );
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      culturas: [],
      talhoes: [],
      animais: [],
      lotes: [],
      fazendas: [],
      safras: [],
      data_inicio: "",
      data_fim: "",
      tipo_rastreio: "todos",
    });
    setErro("");
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  };

  const formatarQuantidade = (valor, unidade) => {
    if (!valor && valor !== 0) return "-";
    return `${valor} ${unidade || ""}`;
  };

  const getStatusClass = (status) => {
    if (status === "Conforme") return "text-green-600";
    if (status === "Pendente") return "text-yellow-600";
    if (status === "Não Conforme") return "text-red-600";
    return "text-gray-600";
  };

  const exportarExcel = () => {
    const workbook = XLSX.utils.book_new();

    if (dados?.insumos?.length > 0) {
      const wsInsumos = XLSX.utils.json_to_sheet(dados.insumos);
      XLSX.utils.book_append_sheet(workbook, wsInsumos, "Insumos Aplicados");
    }

    if (dados?.animais?.length > 0) {
      const wsAnimais = XLSX.utils.json_to_sheet(dados.animais);
      XLSX.utils.book_append_sheet(workbook, wsAnimais, "Historico Animais");
    }

    if (dados?.lotes?.length > 0) {
      const wsLotes = XLSX.utils.json_to_sheet(dados.lotes);
      XLSX.utils.book_append_sheet(workbook, wsLotes, "Lotes Produzidos");
    }

    if (dados?.rastreamento_por_lote?.length > 0) {
      const wsRastreamento = XLSX.utils.json_to_sheet(
        dados.rastreamento_por_lote.map((item) => ({
          ...item,
          insumos: (item.insumos || []).join(", "),
        })),
      );
      XLSX.utils.book_append_sheet(workbook, wsRastreamento, "Rastreamento");
    }

    XLSX.writeFile(workbook, "relatorio_conformidade_rastreabilidade.xlsx");
  };

  // Assim como em Rentabilidade, o relatório original juntava 4 tabelas
  // (insumos, animais, lotes, rastreamento) num único PDF paginado. O
  // utilitário compartilhado exporta uma tabela por chamada, então a
  // exportação passou a seguir a aba selecionada no modal — o que está na
  // tela é o que sai no PDF.
  const exportarPDF = async () => {
    const secoes = {
      insumos: {
        titulo: "Relatório de Conformidade e Rastreabilidade - Insumos Aplicados",
        colunas: [
          { key: "data", label: "Data" },
          { key: "produto", label: "Produto" },
          { key: "talhao", label: "Talhão" },
          { key: "cultura", label: "Cultura" },
          { key: "dose", label: "Dose" },
          { key: "area", label: "Área" },
          { key: "lote", label: "Lote" },
        ],
        dados: (dados?.insumos || []).map((item) => ({
          data: item.data || "-",
          produto: item.produto || "-",
          talhao: item.talhao || "-",
          cultura: item.cultura || "-",
          dose: item.dose || "-",
          area: `${item.area_aplicada || 0} ha`,
          lote: item.lote || "-",
        })),
      },
      animais: {
        titulo: "Relatório de Conformidade e Rastreabilidade - Animais",
        colunas: [
          { key: "identificacao", label: "ID" },
          { key: "nome", label: "Nome" },
          { key: "lote", label: "Lote" },
          { key: "categoria", label: "Categoria" },
          { key: "data_nascimento", label: "Data Nasc." },
          { key: "peso", label: "Peso" },
          { key: "status", label: "Status" },
          { key: "origem", label: "Origem" },
        ],
        dados: (dados?.animais || []).map((item) => ({
          identificacao: item.identificacao || "-",
          nome: item.nome || "-",
          lote: item.lote || "-",
          categoria: item.categoria || "-",
          data_nascimento: formatarData(item.data_nascimento),
          peso: `${item.peso || 0} kg`,
          status: item.status || "-",
          origem: item.origem || "-",
        })),
      },
      lotes: {
        titulo: "Relatório de Conformidade e Rastreabilidade - Lotes Produzidos",
        colunas: [
          { key: "lote", label: "Lote" },
          { key: "fazenda", label: "Fazenda" },
          { key: "qtd_animais", label: "Qtd. Animais" },
          { key: "data_producao", label: "Data Produção" },
          { key: "quantidade", label: "Quantidade" },
          { key: "destino", label: "Destino" },
          { key: "codigo_rastreio", label: "Cód. Rastreio" },
        ],
        dados: (dados?.lotes || []).map((item) => ({
          lote: item.lote || "-",
          fazenda: item.fazenda || "-",
          qtd_animais: item.area || 0,
          data_producao: formatarData(item.data_producao),
          quantidade: formatarQuantidade(item.quantidade, item.unidade),
          destino: item.destino || "-",
          codigo_rastreio: item.codigo_rastreio || "-",
        })),
      },
      rastreamento: {
        titulo: "Relatório de Conformidade e Rastreabilidade - Rastreamento por Lote",
        colunas: [
          { key: "lote", label: "Lote" },
          { key: "codigo_rastreio", label: "Cód. Rastreio" },
          { key: "data_producao", label: "Data Produção" },
          { key: "talhao", label: "Talhão" },
          { key: "insumos", label: "Insumos" },
          { key: "destino", label: "Destino" },
        ],
        dados: (dados?.rastreamento_por_lote || []).map((item) => ({
          lote: item.lote || "-",
          codigo_rastreio: item.codigo_rastreio || "-",
          data_producao: formatarData(item.data_producao),
          talhao: item.talhao || "-",
          insumos: (item.insumos || []).join(", ") || "-",
          destino: item.destino || "-",
        })),
      },
    };

    const secao = secoes[abaAtiva] || secoes.insumos;

    await exportarRelatorioPDF({
      titulo: secao.titulo,
      colunas: secao.colunas,
      dados: secao.dados,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
    });
  };

  const imprimir = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Conformidade e Rastreabilidade</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            h2 { color: #555; font-size: 14px; margin-top: 20px; }
            .resumo-cards { display: flex; gap: 15px; margin-bottom: 25px; flex-wrap: wrap; }
            .card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; min-width: 140px; }
            .card .valor { font-size: 22px; font-weight: bold; margin-top: 5px; }
            .card .label { font-size: 11px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 25px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
          </style>
        </head>
        <body>
          <h1>Relatorio de Conformidade e Rastreabilidade</h1>
          <p>Gerado em: ${new Date().toLocaleString()}</p>
          
          <div class="resumo-cards">
            <div class="card"><div class="label">Insumos Aplicados</div><div class="valor">${dados?.resumo?.total_insumos_aplicados || 0}</div></div>
            <div class="card"><div class="label">Animais Rastreados</div><div class="valor">${dados?.resumo?.total_animais_rastreados || 0}</div></div>
            <div class="card"><div class="label">Lotes Produzidos</div><div class="valor">${dados?.resumo?.total_lotes_produzidos || 0}</div></div>
          </div>

          <h2>📋 Insumos Aplicados</h2>
          <table><thead><tr><th>DATA</th><th>PRODUTO</th><th>TALHAO</th><th>CULTURA</th><th>DOSE</th><th>AREA</th></tr></thead>
          <tbody>${(dados?.insumos || [])
            .map(
              (item) => `
            <tr><td>${item.data || "-"}</td><td>${item.produto || "-"}</td><td>${item.talhao || "-"}</td><td>${item.cultura || "-"}</td><td>${item.dose || "-"}</td><td>${item.area_aplicada || 0} ha</td></tr>
          `,
            )
            .join("")}</tbody></table>

          <h2>🐄 Animais</h2>
          <table><thead><tr><th>ID</th><th>NOME</th><th>LOTE</th><th>CATEGORIA</th><th>DATA NASC</th><th>PESO</th></tr></thead>
          <tbody>${(dados?.animais || [])
            .map(
              (item) => `
            <tr><td>${item.identificacao || "-"}</td><td>${item.nome || "-"}</td><td>${item.lote || "-"}</td><td>${item.categoria || "-"}</td><td>${formatarData(item.data_nascimento)}</td><td>${item.peso || 0} kg</td></tr>
          `,
            )
            .join("")}</tbody></table>

          <h2>📦 Lotes Produzidos</h2>
          <table><thead><tr><th>LOTE</th><th>FAZENDA</th><th>QTD. ANIMAIS</th><th>DATA PRODUCAO</th><th>DESTINO</th><th>COD. RASTREIO</th></tr></thead>
          <tbody>${(dados?.lotes || [])
            .map(
              (item) => `
            <tr><td>${item.lote || "-"}</td><td>${item.fazenda || "-"}</td><td>${item.area || 0}</td><td>${formatarData(item.data_producao)}</td><td>${item.destino || "-"}</td><td>${item.codigo_rastreio || "-"}</td></tr>
          `,
            )
            .join("")}</tbody></table>

          <h2>🔗 Rastreamento por Lote</h2>
          <table><thead><tr><th>LOTE</th><th>COD. RASTREIO</th><th>DATA PRODUCAO</th><th>TALHAO</th><th>INSUMOS</th><th>DESTINO</th></tr></thead>
          <tbody>${(dados?.rastreamento_por_lote || [])
            .map(
              (item) => `
            <tr><td>${item.lote || "-"}</td><td>${item.codigo_rastreio || "-"}</td><td>${formatarData(item.data_producao)}</td><td>${item.talhao || "-"}</td><td>${(item.insumos || []).join(", ") || "-"}</td><td>${item.destino || "-"}</td></tr>
          `,
            )
            .join("")}</tbody></table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); }<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const MultiSelect = ({
    label,
    options,
    selectedValues,
    fieldName,
    refProp,
  }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter((opt) =>
        selectedArray.includes(opt.id),
      );
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2)
        return selected
          .map((s) => s.nome || s.numero_identificacao || s.nome_codigo)
          .join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() =>
            setOpenDropdown(openDropdown === fieldName ? null : fieldName)
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => handleClearAll(fieldName)}
                className="text-sm text-brand-green hover:opacity-80 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={() => handleSelectAll(fieldName, optionsArray)}
                className="text-sm text-brand-green hover:opacity-80"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.map((option) => (
              <label
                key={option.id}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedArray.includes(option.id)}
                  onChange={() => handleMultiSelect(fieldName, option.id)}
                  className="mr-3 h-4 w-4 text-brand-green rounded"
                />
                <span className="text-gray-700">
                  {option.nome ||
                    option.numero_identificacao ||
                    option.nome_codigo}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={MdVerified}
            titulo="Relatório de Conformidade e Rastreabilidade"
            descricao="Histórico completo de insumos aplicados, animais rastreados e lotes produzidos"
          />

          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MultiSelect
                label="Culturas"
                options={culturas}
                selectedValues={filtros.culturas}
                fieldName="culturas"
                refProp={dropdownRefs.culturas}
              />
              <MultiSelect
                label="Talhoes"
                options={talhoes}
                selectedValues={filtros.talhoes}
                fieldName="talhoes"
                refProp={dropdownRefs.talhoes}
              />
              <MultiSelect
                label="Animais"
                options={animais}
                selectedValues={filtros.animais}
                fieldName="animais"
                refProp={dropdownRefs.animais}
              />
              <MultiSelect
                label="Lotes"
                options={lotes}
                selectedValues={filtros.lotes}
                fieldName="lotes"
                refProp={dropdownRefs.lotes}
              />
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />
              <MultiSelect
                label="Safras"
                options={safras}
                selectedValues={filtros.safras}
                fieldName="safras"
                refProp={dropdownRefs.safras}
              />

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Rastreio
                </label>
                <select
                  name="tipo_rastreio"
                  value={filtros.tipo_rastreio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                >
                  <option value="todos">Todos</option>
                  <option value="insumos">Insumos Aplicados</option>
                  <option value="animais">Animais</option>
                  <option value="lotes">Lotes Produzidos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {erro && (
              <div className="mt-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{ maxHeight: "calc(100vh - 4rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                Relatorio de Conformidade e Rastreabilidade
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4 overflow-x-auto">
                <button
                  onClick={() => setAbaAtiva("insumos")}
                  className={`py-3 px-4 font-medium whitespace-nowrap ${abaAtiva === "insumos" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                >
                  🌱 Insumos Aplicados
                </button>
                <button
                  onClick={() => setAbaAtiva("animais")}
                  className={`py-3 px-4 font-medium whitespace-nowrap ${abaAtiva === "animais" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500"}`}
                >
                  🐄 Animais
                </button>
                <button
                  onClick={() => setAbaAtiva("lotes")}
                  className={`py-3 px-4 font-medium whitespace-nowrap ${abaAtiva === "lotes" ? "border-b-2 border-purple-500 text-purple-600" : "text-gray-500"}`}
                >
                  📦 Lotes Produzidos
                </button>
                <button
                  onClick={() => setAbaAtiva("rastreamento")}
                  className={`py-3 px-4 font-medium whitespace-nowrap ${abaAtiva === "rastreamento" ? "border-b-2 border-orange-500 text-orange-600" : "text-gray-500"}`}
                >
                  🔗 Rastreamento
                </button>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {/* Aba Insumos */}
              {abaAtiva === "insumos" && (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <CartaoEstatistica
                      icone={FiFilter}
                      rotulo="Total de Aplicações"
                      valor={dados?.insumos?.length || 0}
                      cor="azul"
                    />
                    <CartaoEstatistica
                      icone={FiMapPin}
                      rotulo="Área Total Aplicada"
                      valor={`${dados?.insumos
                        ?.reduce((acc, i) => acc + (i.area_aplicada || 0), 0)
                        .toFixed(1)} ha`}
                      cor="verde"
                    />
                  </div>

                  {dados?.insumos?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DATA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              PRODUTO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              PRINCIPIO ATIVO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              TALHAO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CULTURA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DOSE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              AREA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              LOTE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              RESPONSAVEL
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.insumos || []).map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-100 hover:bg-gray-50 transition"
                            >
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.data || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                {item.produto || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {item.principio_ativo || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.talhao || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.cultura || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.dose || "-"} L
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.area_aplicada || 0} ha
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.lote || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.responsavel || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio
                      titulo="Nenhum insumo encontrado"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}
                </div>
              )}

              {/* Aba Animais */}
              {abaAtiva === "animais" && (
                <div>
                  {dados?.animais?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              IDENTIFICACAO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              NOME
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              LOTE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CATEGORIA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              RACA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DATA NASCIMENTO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              PESO ATUAL
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              STATUS
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              ORIGEM
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.animais || []).map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-100 hover:bg-gray-50 transition"
                            >
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                {item.identificacao || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.nome || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.lote || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.categoria || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.raca || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatarData(item.data_nascimento)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.peso || 0} kg
                              </td>
                              <td
                                className={`px-4 py-3 text-sm ${getStatusClass(item.status)}`}
                              >
                                {item.status || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.origem || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio
                      titulo="Nenhum animal encontrado"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}
                </div>
              )}

              {/* Aba Lotes */}
              {abaAtiva === "lotes" && (
                <div>
                  {dados?.lotes?.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              LOTE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              FAZENDA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CULTURA
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              QTD. ANIMAIS
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DATA PRODUCAO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              QUANTIDADE
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              DESTINO
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              CODIGO RASTREIO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dados?.lotes || []).map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-100 hover:bg-gray-50 transition"
                            >
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                {item.lote || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.fazenda || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.cultura || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.area || 0}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatarData(item.data_producao)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatarQuantidade(
                                  item.quantidade,
                                  item.unidade,
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.destino || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-xs text-gray-600">
                                {item.codigo_rastreio || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio
                      titulo="Nenhum lote encontrado"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}
                </div>
              )}

              {/* Aba Rastreamento */}
              {abaAtiva === "rastreamento" && (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <FiLink size={22} className="text-green-700" />
                      <h4 className="text-lg font-semibold text-green-800">
                        Cadeia de Rastreabilidade
                      </h4>
                    </div>
                    <p className="text-green-700 text-sm">
                      Rastreamento completo desde a aplicação de insumos até o
                      produto final
                    </p>
                  </div>

                  {(dados?.rastreamento_por_lote || []).length === 0 && (
                    <EstadoVazio
                      titulo="Nenhum rastreamento encontrado"
                      descricao="Ajuste os filtros selecionados e tente novamente."
                    />
                  )}

                  <div className="space-y-4">
                    {(dados?.rastreamento_por_lote || []).map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-white border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-bold text-gray-800">
                            Lote: {item.lote || "-"}
                          </h5>
                          <span className="text-xs text-gray-500">
                            Código: {item.codigo_rastreio || "-"}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 mb-1">
                              📅 Data Produção
                            </div>
                            <div className="font-medium">
                              {formatarData(item.data_producao)}
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 mb-1">
                              🌾 Cultura
                            </div>
                            <div className="font-medium">
                              {item.cultura || "-"}
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 mb-1">
                              📍 Talhão
                            </div>
                            <div className="font-medium">
                              {item.talhao || "-"}
                            </div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="text-xs text-gray-500 mb-2">
                            📋 Insumos Aplicados
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(item.insumos || []).map((insumo, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                              >
                                {insumo}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500 mb-2">
                            📦 Destino
                          </div>
                          <div className="text-sm">{item.destino || "-"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioConformidadeRastreabilidade;
